import moment from 'moment';

import Model from 'src/storage/base';
import constants from 'src/constants';
import AlertManager from 'components/alert';
import { lang, upperFirstCharAll, userFullName } from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';

import { STEPS, BOOK_TYPE } from './constants';
import frontBookConfig from './config.front';

export { STEPS, BOOK_TYPE };

const API_VALIDATION = {
  doctorDetail: { username: 'string' },
  appointmentDetail: { id: 'string' },
  createAppointment: {
    clinicId: 'clinicId',
    partnerId: 'userId',
    slotId: 'string',
    disease: 'disease',
    symptoms: 'symptoms',
    answers: 'answers'
  },
  createAppointmentOffice: { clinicId: 'clinicId', partnerId: 'userId', slotId: 'string' },
  getSlots: { from: 'number', to: 'number' },
  getAvailableSlots: { from: 'number', to: 'number' },
  rescheduleAppointment: { id: 'string' },
  checkSlotAvailable: { slotId: 'string', partnerId: 'userId' },
  hospital: {}
};

const buildApiConfig = (urls) =>
  Object.keys(API_VALIDATION).reduce((result, name) => {
    result[name] = { url: urls[name], validation: API_VALIDATION[name], cancellable: true };
    return result;
  }, {});

/**
 * The booking model. Endpoints and small behaviour differences between the
 * front and admin apps come from `config` (see config.front.js / config.admin.js).
 */
export class BookModel extends Model {
  constructor(config = frontBookConfig) {
    super();
    this.config = config;
    this.id = config.modelId;
    this.apiConfig = buildApiConfig(config.api);
    this._events = {
      profile: config.profileHandler,
      location: 'initLocation'
    };
  }

  defaultState = () => ({
    appointment: null,
    doctor: null,
    doctorId: null,
    patientId: null,
    step: STEPS.SLOTS,
    disease: '',
    symptoms: '',
    payNow: false,
    payLifeapp: false,
    answers: [],
    files: [],
    selectedSlot: null,
    selectedDate: null,
    selectedType: null,
    availableSlots: [],
    slots: [],
    historySteps: []
  });

  refreshOnInit = false;
  refreshOnSubscribe = false;

  initLocation = (data) => {
    this.cacheState = { tempCountryAddress: data.country };
    this.setState({ tempCountryAddress: data.country });
  };

  handleProfile = (profile) => {
    this.setState({ user: profile });
  };

  loadDoctor = (username, id) => {
    return new Promise((resolve) =>
      this.api(
        'doctorDetail',
        { username, id, patientId: this.state.patientId },
        (error, doctor) => {
          if (!error) resolve(doctor);
          else resolve(null);
        }
      )
    );
  };

  loadAppointmentDetail = (id) => {
    return new Promise((resolve) =>
      this.api('appointmentDetail', { id }, (error, appointment) => {
        if (!error) resolve(appointment);
        else resolve(null);
      })
    );
  };

  getAvailableSlots = async (doctorId) => {
    const date = Date.now();
    let res = null;
    await this.api(
      'getAvailableSlots',
      {
        id: doctorId,
        from: date,
        limit: 1,
        skip: 0,
        to: 0
      },
      (err, data) => {
        res = !err ? data : [];
      }
    );

    return res ? res[0] : null;
  };

  getSlots = (selectedSlot, cb) => {
    this.setState({
      availableSlots: []
    });
    const startDate = selectedSlot ? moment(selectedSlot.startDate) : moment();

    selectedSlot = selectedSlot || { startDate: startDate.valueOf() };
    this.api(
      'getSlots',
      {
        id: this.state.doctor?.id,
        from: Math.max(startDate.startOf('month').valueOf(), moment().valueOf()),
        to: startDate.endOf('month').valueOf()
      },
      (err, res, success) => {
        if (success) this.setSlots(res, selectedSlot);
        cb && cb(err, res, success);
      }
    );
  };

  handleChangeSelectedMonth = (startDate, cb) => {
    this.getSlots(
      {
        startDate
      },
      cb
    );
  };

  handleChangeSelectedDay = (selectedDay) => {
    this.setState({ selectedSlot: null, selectedDay, selectedType: null });
  };

  handleChangeSelectedSlot = (selectedSlot) => {
    this.setState({
      selectedSlot,
      selectedDay: selectedSlot?.startDate,
      selectedType: selectedSlot?.appointmentTypes[0]
    });
  };

  setSlots = (slots, selectedSlot) => {
    const availableSlot = slots.find(
      (slot) => !slot.patientId && moment(slot.startDate).isSameOrAfter(moment(), 'day')
    );
    let selectedDay = selectedSlot?.startDate;

    selectedSlot = slots.find((slot) => selectedSlot?.id === slot.id) || availableSlot;
    selectedDay = selectedSlot?.startDate || selectedDay;
    this.setState({
      slots: slots.sort((a, b) => a.startDate - b.startDate),
      selectedSlot,
      selectedDay,
      selectedType: selectedSlot?.appointmentTypes[0]
    });
  };

  resetBook = () => {
    this.resetApi();
    this.setState(this.defaultState());
  };

  createAppointment = (cb) => {
    let slot = this.state.selectedSlot;
    let slotId = slot && slot.id;
    let clinicId = slot && slot.clinicId;

    this.api(
      this.state.selectedType === 'in_office' ? 'createAppointmentOffice' : 'createAppointment',
      {
        partnerId: this.state.user.isPatient ? this.state.doctorId : this.state.patientId,
        doctorId: this.state.doctorId,
        slotId,
        clinicId,
        appointmentType: this.state.selectedType,
        disease: this.state.disease,
        symptoms: this.state.symptoms,
        answers: this.state.answers || [],
        files: this.state.files || [],
        payLifeapp: this.state.payLifeapp,
        payNow: this.state.payNow
      },
      (err, res, success) => {
        if (res && res.invoice) this.emit('invoice', res.invoice);
        cb && cb(err, res, success);
      }
    );
  };

  checkValidSlot = (selectedSlot) => {
    selectedSlot = selectedSlot || this.state.selectedSlot;
    if (!selectedSlot || !!selectedSlot.patientId) return 'You need to select an available slot.';
    if (selectedSlot.startDate <= moment().valueOf())
      return 'You can book appointment only future dates.';
    return null;
  };

  getFees = () => {
    const { doctor, selectedSlot } = this.state;
    if (!doctor) return null;
    const feeType = doctor.chargePatientType || 'first';
    const charge =
      (doctor.charges || []).find((c) => c.id === selectedSlot?.hospitalId) || doctor.charge;
    return charge && charge[feeType];
  };

  getCurrency = () => {
    return this.state.doctor?.currency || null;
  };

  rescheduleAppointment = (cb) => {
    let slot = this.state.selectedSlot;
    let slotId = slot && slot.id;

    this.api(
      'rescheduleAppointment',
      {
        id: this.state.appointment.id,
        slotId,
        ...this.config.getRescheduleExtraParams(this.state),
        appointmentType: this.state.selectedType,
        payLifeapp: this.state.payLifeapp,
        payNow: this.state.payNow
      },
      (err, res, success) => {
        cb(res, success);
      }
    );
  };

  nextStep = (step) => {
    this.state.historySteps.push(this.state.step);
    this.setState({ step });
    this.checkSlotAvailable();
  };

  previousStep = () => {
    const step = this.state.historySteps.pop();
    this.setState({ step });
  };

  checkSlotAvailable = (cb) => {
    let { selectedSlot, patientId } = this.state;
    let slotId = selectedSlot && selectedSlot.id;
    let req = {
      partnerId: this.config.getSlotCheckPartnerId(this.state),
      doctorId: this.state.doctorId,
      slotId,
      clinicId: selectedSlot && selectedSlot.clinicId,
      patientId,
      appointmentType: this.state.selectedType
    };
    this.api('checkSlotAvailable', req, (err, res) => {
      if (res?.error) {
        this.emit('checkSlotError', res.error);
        // this.onOpenBookAppointment(this.state.bookParams);
      }
      typeof cb === 'function' && cb(res?.error);
    });
  };

  loadHospital = (id) => {
    this.api(
      'hospital',
      {
        hospitalIds: [id],
        limit: 1,
        userId: this.config.getHospitalOwnerId(this.state)
      },
      (err, res) => {
        if (!err) {
          this.setState({ hospital: res.items[0] });
        }
      }
    );
  };

  isSamePrice = () => {
    const { appointment, selectedType } = this.state;
    if (!appointment) return false;

    const fees = this.getFees();
    const fee = selectedType === 'virtual' ? fees?.virtual : fees?.office;

    return appointment.data.amount === fee;
  };

  checkDisabledDoctor = (doctor) => {
    if (doctor.status === constants.general.userStatus.disabled) {
      if (doctor.id === this.state.user.id) this.emit('checkDisabledAccount', this.state.user);
      else
        AlertManager.popup(AlertManager.TYPES.INFO, {
          maxWidth: 450,
          close: false,
          title:
            doctor.role.includes('_') && doctor.clinicName
              ? lang.t(LANG_CONST.notice.disabled_clinic_doctor, {
                  clinic: upperFirstCharAll(doctor.clinicName),
                  doctor: upperFirstCharAll(userFullName(doctor))
                })
              : lang.t(LANG_CONST.notice.disabled_doctor, {
                  doctor: upperFirstCharAll(userFullName(doctor))
                }),

          actionButton: {
            label: LANG_CONST.buttons.ok,
            action: (props) => props.hide()
          }
        });
      return true;
    }

    return false;
  };
}

// Default instance for the front app (kept so existing imports keep working)
const model = new BookModel(frontBookConfig);
export default model;
